import { t } from 'i18next';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useHandlers } from '../../../context/page-context';
import { ApiResponseError } from '../../../types/errors';
import { useThemeEditorApi } from '../hooks/use-theme-editor-api';
import type {
  CoreFiles,
  FileRevision,
  FileType,
  OpenFile,
  ThemeFile,
  ThemeWithFiles,
} from '../types/theme';

type State = {
  theme: ThemeWithFiles | null;
  coreFiles: CoreFiles;
  openFiles: Record<string, OpenFile>;
  tabOrder: string[];
  focusedTab: string | undefined;
  /** Initial theme load in progress. */
  initialising: boolean;
  /** A blocking action (open/save/delete/create) is in progress. */
  loading: boolean;
};

type Action =
  | { type: 'INIT_START' }
  | {
      type: 'INIT_DONE';
      theme: ThemeWithFiles;
      coreFiles: CoreFiles;
      openFiles: Record<string, OpenFile>;
      tabOrder: string[];
      focusedTab: string | undefined;
    }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_THEME'; theme: ThemeWithFiles }
  | { type: 'SET_FOCUSED'; id: string }
  | { type: 'OPEN_FILE'; id: string; file: OpenFile }
  | { type: 'CLOSE_FILE'; id: string }
  | { type: 'REPLACE_FILE'; oldId: string; newId: string; file: OpenFile }
  | { type: 'UPDATE_CONTENTS'; id: string; contents: string };

const INITIAL_STATE: State = {
  theme: null,
  coreFiles: {},
  openFiles: {},
  tabOrder: [],
  focusedTab: undefined,
  initialising: true,
  loading: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'INIT_START':
      return { ...INITIAL_STATE, initialising: true };
    case 'INIT_DONE':
      return {
        ...state,
        theme: action.theme,
        coreFiles: action.coreFiles,
        openFiles: action.openFiles,
        tabOrder: action.tabOrder,
        focusedTab: action.focusedTab,
        initialising: false,
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_THEME':
      return { ...state, theme: action.theme };
    case 'SET_FOCUSED':
      return { ...state, focusedTab: action.id };
    case 'OPEN_FILE':
      return {
        ...state,
        openFiles: { ...state.openFiles, [action.id]: action.file },
        tabOrder: state.tabOrder.includes(action.id)
          ? state.tabOrder
          : [...state.tabOrder, action.id],
        focusedTab: action.id,
      };
    case 'CLOSE_FILE': {
      const tabOrder = state.tabOrder.filter((id) => id !== action.id);
      const openFiles = { ...state.openFiles };
      delete openFiles[action.id];
      const focusedTab = state.focusedTab === action.id ? tabOrder[0] : state.focusedTab;
      return { ...state, openFiles, tabOrder, focusedTab };
    }
    case 'REPLACE_FILE': {
      const openFiles = { ...state.openFiles };
      delete openFiles[action.oldId];
      openFiles[action.newId] = action.file;
      const tabOrder = state.tabOrder.map((id) => (id === action.oldId ? action.newId : id));
      return { ...state, openFiles, tabOrder, focusedTab: action.newId };
    }
    case 'UPDATE_CONTENTS': {
      const existing = state.openFiles[action.id];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        openFiles: {
          ...state.openFiles,
          [action.id]: { ...existing, contents: action.contents, unsavedChanges: true },
        },
      };
    }
    default:
      return state;
  }
}

export type ThemeEditorContextValue = {
  theme: ThemeWithFiles | null;
  coreFiles: CoreFiles;
  openFiles: Record<string, OpenFile>;
  tabOrder: string[];
  focusedTab: string | undefined;
  focusedFile: OpenFile | undefined;
  unsavedCount: number;
  initialising: boolean;
  loading: boolean;
  openFile: (file: ThemeFile) => Promise<void>;
  closeFile: (id: string) => void;
  setFocusedTab: (id: string) => void;
  updateContents: (id: string, contents: string) => void;
  saveFocused: () => Promise<void>;
  deleteFocused: () => Promise<string | null>;
  createNewFile: (type: FileType, slug: string) => Promise<void>;
  changeFileVersion: (revision: FileRevision) => Promise<void>;
};

const ThemeEditorContext = createContext<ThemeEditorContextValue | undefined>(undefined);

export function useThemeEditor(): ThemeEditorContextValue {
  const context = useContext(ThemeEditorContext);
  if (!context) {
    throw new Error('useThemeEditor must be used within a ThemeEditorProvider');
  }
  return context;
}

function groupCoreFiles(files: { type: FileType; slug: string }[]): CoreFiles {
  const grouped: CoreFiles = {};
  for (const file of files) {
    const group = grouped[file.type] ?? [];
    group.push(file.slug);
    grouped[file.type] = group;
  }
  return grouped;
}

export function ThemeEditorProvider({
  themeId,
  initialOpen,
  children,
}: PropsWithChildren<{ themeId: string; initialOpen: string[] }>) {
  const { themeEditorApi } = useThemeEditorApi();
  const { navigationHandler, notificationHandler } = useHandlers();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  // Latest state for reads inside async callbacks without stale closures.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Initial open tabs only matter on first load.
  const initialOpenRef = useRef(initialOpen);

  const loadFile = useCallback(
    async (file: ThemeFile): Promise<OpenFile> => {
      const [{ contents }, history] = await Promise.all([
        themeEditorApi.getFileContents(themeId, file.type, file.slug, file.id),
        themeEditorApi.getFileHistory(themeId, file.type, file.slug),
      ]);
      return {
        id: file.id,
        type: file.type,
        slug: file.slug,
        created_at: file.created_at,
        contents,
        history,
        unsavedChanges: false,
      };
    },
    [themeEditorApi, themeId],
  );

  const syncUrl = useCallback(
    (tabOrder: string[]) => {
      navigationHandler.navigate(`/configuration/theme/editor/${themeId}`, {
        query: tabOrder.length ? { open: tabOrder.join(',') } : {},
        replace: true,
      });
    },
    [navigationHandler, themeId],
  );

  // Initial load: core files, theme, and any tabs from `?open=`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once per themeId
  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'INIT_START' });

    (async () => {
      const [core, theme] = await Promise.all([
        themeEditorApi.getCore(),
        themeEditorApi.getTheme(themeId),
      ]);

      const toOpen = theme.files.filter((file) => initialOpenRef.current.includes(file.id));
      const loaded = await Promise.all(toOpen.map((file) => loadFile(file)));

      if (cancelled) {
        return;
      }

      const openFiles: Record<string, OpenFile> = {};
      const tabOrder: string[] = [];
      for (const file of loaded) {
        if (file.id) {
          openFiles[file.id] = file;
          tabOrder.push(file.id);
        }
      }

      dispatch({
        type: 'INIT_DONE',
        theme,
        coreFiles: groupCoreFiles(core.files),
        openFiles,
        tabOrder,
        focusedTab: tabOrder[0],
      });
    })().catch(() => {
      if (!cancelled) {
        notificationHandler.error();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [themeId, themeEditorApi, loadFile]);

  const openFile = useCallback(
    async (file: ThemeFile) => {
      if (file.id in stateRef.current.openFiles) {
        dispatch({ type: 'SET_FOCUSED', id: file.id });
        return;
      }
      dispatch({ type: 'SET_LOADING', loading: true });
      try {
        const openFile = await loadFile(file);
        dispatch({ type: 'OPEN_FILE', id: file.id, file: openFile });
        syncUrl([...stateRef.current.tabOrder, file.id]);
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [loadFile, syncUrl],
  );

  const closeFile = useCallback(
    (id: string) => {
      dispatch({ type: 'CLOSE_FILE', id });
      syncUrl(stateRef.current.tabOrder.filter((tab) => tab !== id));
    },
    [syncUrl],
  );

  const setFocusedTab = useCallback((id: string) => {
    dispatch({ type: 'SET_FOCUSED', id });
  }, []);

  const updateContents = useCallback((id: string, contents: string) => {
    dispatch({ type: 'UPDATE_CONTENTS', id, contents });
  }, []);

  const saveFocused = useCallback(async () => {
    const { theme, focusedTab, openFiles } = stateRef.current;
    const file = focusedTab ? openFiles[focusedTab] : undefined;
    if (!theme || !file?.id) {
      return;
    }

    dispatch({ type: 'SET_LOADING', loading: true });
    try {
      const saved = await themeEditorApi.saveFile(theme.id, file.type, file.slug, file.contents);
      const newFile: OpenFile = {
        ...file,
        id: saved.id,
        created_at: saved.created_at,
        unsavedChanges: false,
        history: [{ id: saved.id, created_at: saved.created_at }, ...file.history],
      };
      dispatch({ type: 'REPLACE_FILE', oldId: file.id, newId: saved.id, file: newFile });
      syncUrl(stateRef.current.tabOrder.map((id) => (id === file.id ? saved.id : id)));
    } catch (err) {
      if (
        ApiResponseError.isApiResponseError(err, ApiResponseError.errorSchemas.detail) &&
        err.status === 400
      ) {
        const detail = err.data.detail;
        if (detail) {
          notificationHandler.error(
            `${t('features:themes.pages.theme_editor_page.notifications.invalid_syntax')} - ${detail}`,
          );
          return;
        }
      }
      notificationHandler.error();
    } finally {
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  }, [themeEditorApi, notificationHandler, syncUrl]);

  const deleteFocused = useCallback(async (): Promise<string | null> => {
    const { theme, focusedTab, openFiles } = stateRef.current;
    const file = focusedTab ? openFiles[focusedTab] : undefined;
    if (!theme || !file) {
      return null;
    }

    dispatch({ type: 'SET_LOADING', loading: true });
    try {
      await themeEditorApi.deleteFile(theme.id, file.type, file.slug);
      const updatedTheme: ThemeWithFiles = {
        ...theme,
        files: theme.files.filter((themeFile) => themeFile.id !== file.id),
      };
      dispatch({ type: 'SET_THEME', theme: updatedTheme });
      if (file.id) {
        dispatch({ type: 'CLOSE_FILE', id: file.id });
        syncUrl(stateRef.current.tabOrder.filter((tab) => tab !== file.id));
      }
      return null;
    } catch (err) {
      const message =
        err instanceof ApiResponseError
          ? ((err.data as { message?: string } | undefined)?.message ?? t('errors:generic'))
          : t('errors:generic');
      return message;
    } finally {
      dispatch({ type: 'SET_LOADING', loading: false });
    }
  }, [themeEditorApi, syncUrl]);

  const createNewFile = useCallback(
    async (type: FileType, slug: string) => {
      const { theme } = stateRef.current;
      if (!theme) {
        return;
      }
      dispatch({ type: 'SET_LOADING', loading: true });
      try {
        const contents =
          type === 'language' ? '{}' : (await themeEditorApi.getCoreFile(type, slug)).contents;
        const saved = await themeEditorApi.saveFile(theme.id, type, slug, contents);
        const refreshed = await themeEditorApi.getTheme(theme.id);
        dispatch({ type: 'SET_THEME', theme: refreshed });

        const newFile: OpenFile = {
          id: saved.id,
          type,
          slug,
          created_at: saved.created_at,
          contents,
          history: [{ id: saved.id, created_at: saved.created_at }],
          unsavedChanges: false,
        };
        dispatch({ type: 'OPEN_FILE', id: saved.id, file: newFile });
        syncUrl([...stateRef.current.tabOrder, saved.id]);
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [themeEditorApi, syncUrl],
  );

  const changeFileVersion = useCallback(
    async (revision: FileRevision) => {
      const { theme, focusedTab, openFiles } = stateRef.current;
      const current = focusedTab ? openFiles[focusedTab] : undefined;
      if (!theme || !current || revision.id === current.id) {
        return;
      }
      dispatch({ type: 'SET_LOADING', loading: true });
      try {
        const { contents } = await themeEditorApi.getFileContents(
          theme.id,
          current.type,
          current.slug,
          revision.id,
        );
        const newFile: OpenFile = {
          ...current,
          id: revision.id,
          created_at: revision.created_at,
          contents,
          unsavedChanges: true,
        };
        if (current.id) {
          dispatch({ type: 'REPLACE_FILE', oldId: current.id, newId: revision.id, file: newFile });
          syncUrl(stateRef.current.tabOrder.map((id) => (id === current.id ? revision.id : id)));
        }
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    },
    [themeEditorApi, syncUrl],
  );

  const focusedFile = state.focusedTab ? state.openFiles[state.focusedTab] : undefined;
  const unsavedCount = useMemo(
    () => Object.values(state.openFiles).filter((file) => file.unsavedChanges).length,
    [state.openFiles],
  );

  const value: ThemeEditorContextValue = {
    theme: state.theme,
    coreFiles: state.coreFiles,
    openFiles: state.openFiles,
    tabOrder: state.tabOrder,
    focusedTab: state.focusedTab,
    focusedFile,
    unsavedCount,
    initialising: state.initialising,
    loading: state.loading,
    openFile,
    closeFile,
    setFocusedTab,
    updateContents,
    saveFocused,
    deleteFocused,
    createNewFile,
    changeFileVersion,
  };

  return <ThemeEditorContext.Provider value={value}>{children}</ThemeEditorContext.Provider>;
}
